/* RESPONSIBLE TEAM: team-reporting */

import { tracked } from '@glimmer/tracking';
import type { ExportRule } from 'embercom/models/reporting/dataset-export-schedule';
import type ScheduledExternalShare from 'embercom/models/reporting/scheduled-external-share';

interface FormSnapshot {
  name: string;
  message: string;
  exportRule?: ExportRule;
  userIds: string[];
  roleIds: string[];
  emails: string[];
}

/**
 * Manages form state for schedule delivery drawer, including tracking
 * original values for dirty checking in edit mode.
 */
export default class ScheduleFormState {
  @tracked scheduleName = '';
  @tracked scheduleMessage = '';
  @tracked exportRule?: ExportRule;
  @tracked userIds: string[] = [];
  @tracked roleIds: string[] = [];
  @tracked emails: string[] = [];

  private originalSnapshot?: FormSnapshot;

  /**
   * Initialize form with values from an existing schedule (edit mode)
   */
  initializeFromSchedule(schedule: ScheduledExternalShare): void {
    this.setFields(schedule.name, schedule.message, schedule.exportRule);
    this.originalSnapshot = this.createSnapshot(
      schedule.name,
      schedule.message,
      schedule.exportRule,
      schedule.recipients?.userIds?.map(String) || [],
      schedule.recipients?.roles?.map(String) || [],
      schedule.recipients?.emails || [],
    );
  }

  /**
   * Initialize form with default values (create mode)
   */
  initializeForCreate(defaultExportRule: ExportRule): void {
    this.setFields('', '', defaultExportRule);
    this.userIds = [];
    this.roleIds = [];
    this.emails = [];
    this.originalSnapshot = undefined;
  }

  /**
   * Update recipients
   */
  updateRecipients(userIds: string[], roleIds: string[], emails: string[]): void {
    this.userIds = userIds;
    this.roleIds = roleIds;
    this.emails = emails;
  }

  /**
   * Check if form has unsaved changes compared to original values
   */
  hasUnsavedChanges(isEditMode: boolean): boolean {
    if (!isEditMode || !this.originalSnapshot) {
      return true;
    }

    let currentSnapshot = this.createSnapshot(
      this.scheduleName,
      this.scheduleMessage,
      this.exportRule,
      this.userIds,
      this.roleIds,
      this.emails,
    );

    return JSON.stringify(currentSnapshot) !== JSON.stringify(this.originalSnapshot);
  }

  /**
   * Reset form to initial state
   */
  reset(): void {
    this.setFields('', '', undefined);
    this.userIds = [];
    this.roleIds = [];
    this.emails = [];
    this.originalSnapshot = undefined;
  }

  private setFields(name: string, message: string, exportRule?: ExportRule): void {
    this.scheduleName = name;
    this.scheduleMessage = message;
    this.exportRule = exportRule;
  }

  private createSnapshot(
    name: string,
    message: string,
    exportRule: ExportRule | undefined,
    userIds: string[],
    roleIds: string[],
    emails: string[],
  ): FormSnapshot {
    return {
      name,
      message,
      exportRule,
      userIds: [...userIds].sort(),
      roleIds: [...roleIds].sort(),
      emails: [...emails].sort(),
    };
  }
}
